import ReactECharts from 'echarts-for-react'
import { TrendingUp, TrendingDown } from 'lucide-react'

interface DistributionData {
  top1: number
  top3: number
  top5: number
  top10: number
  top30: number
  notRanked: number
}

interface HistoryEntry {
  date: string
  top1: number
  top3: number
  top5: number
  top10: number
  top30: number
  top100: number
  total: number
}

interface PositionDistributionProps {
  distribution: DistributionData
  history: HistoryEntry[]
}

// Ranges affichés dans les mini-cards (tous)
const RANGES = [
  { key: 'top1', label: 'Top 1', color: '#10B981', histKey: 'top1' },
  { key: 'top3', label: 'Top 2-3', color: '#22C55E', histKey: 'top3' },
  { key: 'top5', label: 'Top 4-5', color: '#F97316', histKey: 'top5' },
  { key: 'top10', label: 'Top 6-10', color: '#EC4899', histKey: 'top10' },
  { key: 'top30', label: 'Top 11-30', color: '#8B5CF6', histKey: 'top30' },
  { key: 'notRanked', label: 'Top 31-100', color: '#EF4444', histKey: 'top100' },
] as const

// Ranges inclus dans le graphique (top31-100 exclu — sinon le stack remonte toujours à 100%)
const CHART_RANGES = RANGES.slice(0, 5) as typeof RANGES[number][]

function MiniSpline({ data, color, dates }: { data: number[]; color: string; dates: string[] }) {
  if (data.length < 2) return <div className="w-[80px] h-[32px]" />

  const option = {
    grid: { top: 2, right: 0, bottom: 2, left: 0 },
    xAxis: { type: 'category' as const, show: false, data: dates },
    yAxis: { type: 'value' as const, show: false, min: 0 },
    tooltip: {
      trigger: 'axis' as const,
      confine: true,
      backgroundColor: '#1B2559',
      borderColor: '#1B2559',
      padding: [4, 8],
      textStyle: { color: '#fff', fontSize: 10 },
      formatter: (params: any) => {
        const p = Array.isArray(params) ? params[0] : params
        const d = new Date(p.name)
        const label = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
        return `<span style="color:#94A3B8">${label}</span> <b>${p.value}</b>`
      },
    },
    series: [{
      type: 'line' as const,
      data,
      smooth: true,
      symbol: 'circle',
      symbolSize: 4,
      showSymbol: false,
      lineStyle: { width: 1.5, color },
      itemStyle: { color },
      areaStyle: { color, opacity: 0.08 },
    }],
  }

  return <ReactECharts option={option} style={{ width: '100%', height: 36 }} opts={{ renderer: 'svg' }} />
}

function DeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) return null
  if (delta > 0) return (
    <span className="flex items-center gap-0.5 text-emerald-500 text-[11px] font-semibold">
      <TrendingUp className="w-3 h-3" strokeWidth={2} />
      {delta}
    </span>
  )
  return (
    <span className="flex items-center gap-0.5 text-red-400 text-[11px] font-semibold">
      <TrendingDown className="w-3 h-3" strokeWidth={2} />
      {Math.abs(delta)}
    </span>
  )
}

function StackedAreaChart({ history }: { history: HistoryEntry[] }) {
  if (history.length < 1) {
    return (
      <div className="flex items-center justify-center h-[280px] text-center">
        <div>
          <p className="text-sm text-[#8B8FA7]">Aucune donnée historique</p>
          <p className="text-xs text-[#8B8FA7] mt-1">Les données apparaîtront après plusieurs vérifications</p>
        </div>
      </div>
    )
  }

  const dates = history.map(h => h.date)

  const series = CHART_RANGES.map(range => ({
    name: range.label,
    type: 'line' as const,
    smooth: true,
    symbol: 'circle',
    symbolSize: 6,
    showSymbol: false,
    lineStyle: { width: 2, color: range.color },
    itemStyle: { color: range.color, borderColor: '#fff', borderWidth: 1.5 },
    areaStyle: { color: range.color, opacity: 0.12 },
    emphasis: { focus: 'series' as const },
    data: history.map(h => (h as any)[range.histKey] ?? 0),
  }))

  const option = {
    color: CHART_RANGES.map(r => r.color),
    tooltip: {
      trigger: 'axis' as const,
      backgroundColor: '#1B2559',
      borderColor: '#1B2559',
      textStyle: { color: '#fff', fontSize: 11 },
      confine: true,
      enterable: false,
      axisPointer: {
        type: 'line' as const,
        snap: true,
      },
    },
    legend: {
      data: CHART_RANGES.map(r => r.label),
      bottom: 0,
      itemWidth: 8,
      itemHeight: 8,
      textStyle: { fontSize: 10, color: '#8B8FA7' },
      icon: 'circle',
    },
    grid: { top: 10, right: 16, bottom: 36, left: 36 },
    xAxis: {
      type: 'category' as const,
      data: dates,
      axisLine: { lineStyle: { color: '#E2E4ED' } },
      axisLabel: {
        fontSize: 10,
        color: '#8B8FA7',
        formatter: (val: string) => {
          const d = new Date(val)
          return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
        },
      },
    },
    yAxis: {
      type: 'value' as const,
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#F4F5FA' } },
      axisLabel: { fontSize: 10, color: '#8B8FA7' },
    },
    series,
  }

  return <ReactECharts option={option} style={{ height: 280 }} />
}

export default function PositionDistribution({ distribution, history }: PositionDistributionProps) {
  const deltas: Record<string, number> = {}
  if (history.length >= 2) {
    const last = history[history.length - 1]
    const prev = history[history.length - 2]
    for (const range of RANGES) {
      deltas[range.key] = ((last as any)[range.histKey] ?? 0) - ((prev as any)[range.histKey] ?? 0)
    }
  }

  const miniDates = history.map(h => h.date)

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
      <div className="flex gap-6">
        {/* LEFT: 1/3 width — mini-cards with larger charts */}
        <div className="w-1/3 shrink-0">
          <div className="grid grid-cols-2 gap-px bg-[#E2E4ED] rounded-xl overflow-hidden">
            {RANGES.map(range => {
              const value = (distribution as any)[range.key] ?? 0
              const delta = deltas[range.key] ?? 0
              const miniData = history.length >= 2
                ? history.map(h => (h as any)[range.histKey] ?? 0)
                : [value, value]
              const miniDatesFinal = history.length >= 2 ? miniDates : ['', '']

              return (
                <div key={range.key} className="bg-white p-3 flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[#8B8FA7] font-medium">{range.label}</span>
                  </div>
                  <MiniSpline data={miniData} color={range.color} dates={miniDatesFinal} />
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold text-[#1B2559] tabular-nums">{value}</span>
                    <DeltaBadge delta={delta} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* RIGHT: Stacked area chart */}
        <div className="flex-1 min-w-0">
          <StackedAreaChart history={history} />
        </div>
      </div>
    </div>
  )
}
